import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '@/context/AuthContext';
import {
  Inbox, Loader2, Users, Briefcase, Handshake, TrendingUp,
  Search, ChevronDown, ChevronUp, RefreshCw, Download,
} from 'lucide-react';

const TYPE_CONFIG = {
  loan:       { label: 'Apply Now Leads', color: '#1e73be', bg: '#eaf3fb', icon: Briefcase },
  career:     { label: 'Career Applications', color: '#2ea690', bg: '#e7f6f2', icon: Users },
  partner:    { label: 'Become a Partner Leads', color: '#7c3aed', bg: '#f3f0ff', icon: Handshake },
  mf_inquiry: { label: 'MF Inquiries', color: '#d97706', bg: '#fef9ee', icon: TrendingUp },
};

const AdminLeads = () => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [subsRes, cntRes] = await Promise.all([
        axios.get(`${API}/forms/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/forms/submissions/count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!Array.isArray(subsRes.data) || !cntRes.data || typeof cntRes.data !== 'object' || Array.isArray(cntRes.data)) {
        throw new Error('The leads service returned an unexpected response. Please try again.');
      }
      setSubmissions(subsRes.data);
      setCounts(cntRes.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = submissions.filter((s) => {
    if (filter !== 'all' && s.form_type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const dataStr = JSON.stringify(s.data || {}).toLowerCase();
      if (!dataStr.includes(q)) return false;
    }
    return true;
  });

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const downloadCSV = () => {
    const rows = [['ID', 'Type', 'Submitted At', 'Data']];
    filtered.forEach((s) => {
      rows.push([s.id, s.form_type, s.submitted_at, JSON.stringify(s.data)]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'leads.csv'; a.click();
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Form Submissions</h1>
          <p className="text-slate-600 text-sm mt-1">{total} total entries across all forms</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 transition">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={downloadCSV} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 transition">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {/* Count cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
          <button key={key}
            onClick={() => setFilter(filter === key ? 'all' : key)}
            className={`flex items-center gap-3 p-4 rounded-xl border text-left transition ${filter === key ? 'ring-2 ring-offset-1' : 'hover:shadow-sm'}`}
            style={{ backgroundColor: cfg.bg, borderColor: cfg.color + '33', '--tw-ring-color': cfg.color }}>
            <div className="w-9 h-9 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: '#fff' }}>
              <cfg.icon className="w-4 h-4" style={{ color: cfg.color }} />
            </div>
            <div>
              <p className="font-heading font-bold text-[20px] leading-none" style={{ color: cfg.color }}>
                {counts[key] || 0}
              </p>
              <p className="text-[11px] text-slate-600 mt-0.5">{cfg.label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Search + filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 border border-slate-200 rounded-lg px-3 h-10 bg-white">
          <Search className="w-4 h-4 text-slate-400 flex-none" />
          <input placeholder="Search by name, email, phone…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 outline-none text-sm bg-transparent" />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm text-slate-700 outline-none">
          <option value="all">All Types</option>
          {Object.entries(TYPE_CONFIG).map(([k, c]) => (
            <option key={k} value={k}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Submissions list */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900 text-sm">
            Showing {filtered.length} submission{filtered.length !== 1 ? 's' : ''}
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading submissions...
          </div>
        ) : err ? (
          <div className="p-8 text-center text-red-600">{err}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No submissions yet</p>
             <p className="text-slate-400 text-sm mt-1">Apply Now, Become a Partner, careers, and investment enquiries appear here after they are saved.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((s) => {
              const cfg = TYPE_CONFIG[s.form_type] || TYPE_CONFIG.loan;
              const isExp = expanded === s.id;
              const data = s.data || {};
              const name = data.name || data.fullName || '—';
              const phone = data.phone || data.mobile || '—';
              const email = data.email || '—';
              const date = s.submitted_at ? new Date(s.submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

              return (
                <li key={s.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition text-left"
                    onClick={() => setExpanded(isExp ? null : s.id)}
                  >
                    <div className="w-9 h-9 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: cfg.bg }}>
                      <cfg.icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 truncate">{name}</p>
                        <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{phone} · {email}</p>
                    </div>
                    <div className="text-right hidden md:block flex-none">
                      <p className="text-xs text-slate-500">{date}</p>
                    </div>
                    {isExp ? <ChevronUp className="w-4 h-4 text-slate-400 flex-none" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-none" />}
                  </button>
                  {isExp && (
                    <div className="px-5 pb-5 pt-0 bg-slate-50 border-t border-slate-100">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                        {Object.entries(data).map(([k, v]) => (
                          <div key={k} className="bg-white border border-slate-100 rounded-lg p-3">
                            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-0.5 capitalize">
                              {k.replace(/_/g, ' ')}
                            </p>
                            <p className="text-sm text-slate-800 font-medium break-all">
                              {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v || '—')}
                            </p>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-3">ID: {s.id} · Submitted: {date}</p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
